import React, { useState, useRef } from 'react'
import {
  Megaphone,
  BarChart3,
  Plus,
  X,
  Loader,
  Trash2,
  Clock,
  Mail,
  Calendar,
  MapPin,
  Link as LinkIcon,
} from 'lucide-react'
import { collection, addDoc, doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore'
import { db } from '../utils/firebase'
import { normalizeUrl } from '../utils/announcementContent'

/**
 * Two modals shared between the public Announcements page and the Admin
 * panel's "Manage Announcements" section.
 *
 * - `TypeSelectorModal` lets the user pick between "Announcement" and
 *   "Poll" before opening the editor.
 * - `CreateAnnouncementModal` is the editor itself. It supports both
 *   create and edit modes:
 *     - When `existing` is null/undefined it does `addDoc(announcements)`.
 *     - When `existing` is provided it pre-fills fields and calls
 *       `updateDoc` on that document instead. The author identity and
 *       the original `createdDate` are preserved on edit.
 *
 * The whole `poll.votes` map is preserved on edit so neighbors don't lose
 * their votes if an admin tweaks the title or option labels.
 */

// ============================================================
// Type Selector Modal
// ============================================================
export function TypeSelectorModal({ onClose, onSelect }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-type-selector" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>What would you like to create?</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="type-options">
          <button className="type-option" data-walk="type-selector-announcement" onClick={() => onSelect('announcement')}>
            <div className="type-icon announcement-icon">
              <Megaphone size={28} />
            </div>
            <div className="type-info">
              <h3>Announcement</h3>
              <p>Share news, updates, or information with your neighbors</p>
            </div>
          </button>

          <button className="type-option" onClick={() => onSelect('poll')}>
            <div className="type-icon poll-icon">
              <BarChart3 size={28} />
            </div>
            <div className="type-info">
              <h3>Poll</h3>
              <p>Ask a question and let neighbors vote on options</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Create / Edit Modal
// ============================================================
export function CreateAnnouncementModal({
  type,            // 'announcement' | 'poll' (only used in create mode)
  existing,        // optional announcement doc to edit
  onClose,
  onCreated,
  userProfile,
}) {
  const isEdit = !!existing
  // In edit mode, infer the type from the existing doc.
  const effectiveType = isEdit ? (existing.poll ? 'poll' : 'announcement') : type
  const isPoll = effectiveType === 'poll'

  // Pre-fill from `existing` when editing. For polls we use the question
  // as the title (mirrors how create mode stores title === question).
  const [title, setTitle] = useState(isEdit ? (existing.title || '') : '')
  const [content, setContent] = useState(isEdit ? (existing.content || '') : '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const contentRef = useRef(null)

  // In-modal "Add link" dialog. Both fields are visible at once so
  // authors don't have to click through two native prompts.
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkLabel, setLinkLabel] = useState('')
  const [linkUrl, setLinkUrl] = useState('https://')
  const [linkError, setLinkError] = useState('')
  const linkSelRef = useRef({ start: 0, end: 0 })

  // Toolbar handler: capture the current textarea selection, prefill
  // the label field with the selected text, and open the dialog.
  const handleInsertLink = () => {
    const textarea = contentRef.current
    if (!textarea) return
    const selStart = textarea.selectionStart ?? content.length
    const selEnd = textarea.selectionEnd ?? content.length
    linkSelRef.current = { start: selStart, end: selEnd }
    setLinkLabel(content.substring(selStart, selEnd))
    setLinkUrl('https://')
    setLinkError('')
    setLinkDialogOpen(true)
  }

  const confirmInsertLink = () => {
    const url = normalizeUrl(linkUrl.trim())
    if (!url) {
      setLinkError('Please enter a valid URL (http://, https://, or mailto:).')
      return
    }
    const label = (linkLabel.trim() || url)
    const { start, end } = linkSelRef.current
    const insertion = `[${label}](${url})`
    const newContent = content.substring(0, start) + insertion + content.substring(end)
    setContent(newContent)
    setLinkDialogOpen(false)

    // Restore focus + caret position after React re-render commits.
    requestAnimationFrame(() => {
      const el = contentRef.current
      if (!el) return
      el.focus()
      const caret = start + insertion.length
      el.setSelectionRange(caret, caret)
    })
  }

  // Poll state
  const [pollType, setPollType] = useState(() => {
    if (!isEdit || !existing.poll) return 'yesno'
    return existing.poll.type || 'yesno'
  })
  const [pollOptions, setPollOptions] = useState(() => {
    if (!isEdit || !existing.poll) return ['', '']
    // For edit, pre-fill poll options. yesno polls don't expose options
    // for editing — they stay as Yes/No.
    return existing.poll.options || ['', '']
  })

  // Poll end time state
  const [hasEndTime, setHasEndTime] = useState(() => {
    return !!(isEdit && existing.poll?.expiresAt)
  })
  const [endTimePreset, setEndTimePreset] = useState(() => {
    return isEdit && existing.poll?.expiresAt ? 'custom' : '1d'
  })
  const [customEndTime, setCustomEndTime] = useState(() => {
    if (!isEdit || !existing.poll?.expiresAt) return ''
    const dt = new Date(existing.poll.expiresAt)
    if (isNaN(dt.getTime())) return ''
    // Format as `YYYY-MM-DDTHH:mm` for datetime-local input.
    const tzOffset = dt.getTimezoneOffset() * 60000
    return new Date(dt.getTime() - tzOffset).toISOString().slice(0, 16)
  })

  // Add-to-Calendar state (announcement create-mode only)
  const [addToCalendar, setAddToCalendar] = useState(false)
  // Author opt-out for the resident email blast. Defaults to true so
  // legacy behavior (auto-email on post) is preserved when the checkbox
  // is left alone.
  const [emailNeighbors, setEmailNeighbors] = useState(true)
  const [eventStart, setEventStart] = useState('')
  const [eventEnd, setEventEnd] = useState('')
  const [eventLocation, setEventLocation] = useState('')

  // "Dirty" = the author has typed something they'd lose by closing.
  // In create mode we just check whether any input has non-whitespace
  // content — no need to snapshot initial values because everything
  // starts empty. In edit mode we compare against the pre-filled values
  // so opening + closing without touching anything doesn't nag.
  const isDirty = (() => {
    if (isEdit) {
      if (title !== (existing.title || '')) return true
      if (content !== (existing.content || '')) return true
      if (isPoll) {
        const initialOptions = existing.poll?.options || ['', '']
        if (pollType !== (existing.poll?.type || 'yesno')) return true
        // Length differences count as dirty (added or removed an option).
        if (pollOptions.length !== initialOptions.length) return true
        for (let i = 0; i < pollOptions.length; i++) {
          if ((pollOptions[i] || '') !== (initialOptions[i] || '')) return true
        }
        if (hasEndTime !== !!existing.poll?.expiresAt) return true
      }
      return false
    }
    // Create mode — any content anywhere counts as dirty.
    if (title.trim()) return true
    if (content.trim()) return true
    if (isPoll) {
      if (pollOptions.some(opt => (opt || '').trim())) return true
      if (hasEndTime) return true
    }
    if (!isPoll && addToCalendar) return true
    if (!isPoll && (eventStart || eventEnd || eventLocation.trim())) return true
    return false
  })()

  // Wraps `onClose` with a "you have unsaved changes" confirmation when
  // the form has content the author would lose. Used by every close
  // path — X button, Cancel button, and overlay click — so there's no
  // way to lose work by accident.
  const handleCloseAttempt = () => {
    if (loading) return // Never dismiss mid-submit.
    if (!isDirty) {
      onClose()
      return
    }
    const confirmed = window.confirm(
      isEdit
        ? 'You have unsaved changes. Discard them?'
        : `Discard this ${isPoll ? 'poll' : 'announcement'}?`
    )
    if (confirmed) onClose()
  }

  // Compute the expiration Date from the current preset / custom input
  const computeExpiresAt = () => {
    if (!hasEndTime) return null
    if (endTimePreset === 'custom') {
      if (!customEndTime) return null
      const dt = new Date(customEndTime)
      return isNaN(dt.getTime()) ? null : dt
    }
    const now = new Date()
    const presetMs = {
      '1d': 24 * 60 * 60 * 1000,
      '3d': 3 * 24 * 60 * 60 * 1000,
      '1w': 7 * 24 * 60 * 60 * 1000,
      '2w': 14 * 24 * 60 * 60 * 1000,
    }[endTimePreset]
    return presetMs ? new Date(now.getTime() + presetMs) : null
  }

  const addPollOption = () => {
    if (pollOptions.length < 6) {
      setPollOptions([...pollOptions, ''])
    }
  }

  const removePollOption = (index) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index))
    }
  }

  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptions]
    newOptions[index] = value
    setPollOptions(newOptions)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!title.trim()) {
      setError(isPoll ? 'Please enter your question' : 'Please enter a title')
      return
    }

    // For announcements, content is required
    if (!isPoll && !content.trim()) {
      setError('Please enter the announcement content')
      return
    }

    // Validate poll options if custom
    if (isPoll && pollType === 'custom') {
      const validOptions = pollOptions.filter(opt => opt.trim())
      if (validOptions.length < 2) {
        setError('Please provide at least 2 poll options')
        return
      }
    }

    // Validate end time if set. (Past times are only an error in *create*
    // mode — in edit mode an admin may legitimately leave an existing
    // expired end time alone, e.g. when correcting a typo on a closed
    // poll.)
    let expiresAtDate = null
    if (isPoll && hasEndTime) {
      expiresAtDate = computeExpiresAt()
      if (!expiresAtDate) {
        setError('Please pick an end date and time')
        return
      }
      if (!isEdit && expiresAtDate.getTime() <= Date.now()) {
        setError('End time must be in the future')
        return
      }
    }

    // Validate Add-to-Calendar fields (create-mode announcement only)
    let eventStartDate = null
    let eventEndDate = null
    if (!isEdit && !isPoll && addToCalendar) {
      if (!eventStart) {
        setError('Please pick a start time for the calendar event')
        return
      }
      eventStartDate = new Date(eventStart)
      if (isNaN(eventStartDate.getTime())) {
        setError('Please pick a valid start time')
        return
      }
      if (eventEnd) {
        eventEndDate = new Date(eventEnd)
        if (isNaN(eventEndDate.getTime())) {
          setError('Please pick a valid end time')
          return
        }
        if (eventEndDate.getTime() <= eventStartDate.getTime()) {
          setError('End time must be after start time')
          return
        }
      }
    }

    try {
      setLoading(true)
      setError('')

      if (isEdit) {
        // Build the update payload. We never touch authorId, authorName,
        // createdDate or isPinned here so identity and ordering stay
        // intact across edits.
        const updateData = {
          title: title.trim(),
          content: isPoll ? '' : content.trim(),
          updatedAt: serverTimestamp(),
          editedById: userProfile.email,
          editedByName: userProfile.name,
        }

        if (isPoll && existing.poll) {
          const options = pollType === 'yesno'
            ? ['Yes', 'No']
            : pollOptions.filter(opt => opt.trim())

          updateData.poll = {
            // Preserve the original poll metadata + votes; only update
            // the editable parts.
            ...existing.poll,
            question: title.trim(),
            type: pollType,
            options,
            expiresAt: expiresAtDate ? expiresAtDate.toISOString() : null,
          }
        }

        await updateDoc(doc(db, 'announcements', existing.id), updateData)
      } else {
        const announcementData = {
          title: title.trim(),
          content: isPoll ? '' : content.trim(),
          authorId: userProfile.email,
          authorName: userProfile.name,
          neighborhoodId: userProfile.neighborhoodId,
          createdDate: serverTimestamp(),
          isPinned: false,
          type: isPoll ? 'poll' : 'announcement',
          // Author opt-out for the resident email blast. The Cloud
          // Function `sendAnnouncementEmail` skips the send when this
          // is explicitly false.
          emailNeighbors: emailNeighbors
        }

        if (isPoll) {
          const options = pollType === 'yesno'
            ? ['Yes', 'No']
            : pollOptions.filter(opt => opt.trim())

          announcementData.poll = {
            question: title.trim(), // Use title as the question
            type: pollType,
            options: options,
            votes: {},
            createdAt: new Date().toISOString(),
            expiresAt: expiresAtDate ? expiresAtDate.toISOString() : null
          }
        }

        await addDoc(collection(db, 'announcements'), announcementData)

        // Companion calendar event, if the author opted in.
        if (!isPoll && addToCalendar && eventStartDate) {
          try {
            await addDoc(collection(db, 'events'), {
              neighborhoodId: userProfile.neighborhoodId,
              title: title.trim(),
              description: content.trim(),
              location: eventLocation.trim(),
              startAt: Timestamp.fromDate(eventStartDate),
              endAt: eventEndDate ? Timestamp.fromDate(eventEndDate) : null,
              createdBy: userProfile.email,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            })
          } catch (evErr) {
            // Announcement already posted — surface event failure but
            // don't block the success path.
            console.error('Failed to create companion event:', evErr)
          }
        }
      }

      onCreated()
    } catch (err) {
      console.error('Error saving announcement:', err)
      setError(isEdit ? 'Failed to save changes. Please try again.' : 'Failed to create. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Header / button labels change based on mode + type so the modal reads
  // correctly in all four combinations.
  const headerTitle = isEdit
    ? (isPoll ? 'Edit Poll' : 'Edit Announcement')
    : (isPoll ? 'New Poll' : 'New Announcement')
  const submitLabel = isEdit
    ? 'Save changes'
    : (isPoll ? 'Create Poll' : 'Post Announcement')
  const submittingLabel = isEdit ? 'Saving...' : 'Posting...'

  return (
    <div className="modal-overlay" onClick={handleCloseAttempt}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{headerTitle}</h2>
          {/* All three close paths (X, overlay, Cancel below) route
              through handleCloseAttempt so the "you'll lose your
              draft" confirmation shows regardless of which one the
              author uses. */}
          <button className="close-btn" onClick={handleCloseAttempt} type="button">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}

          {isPoll ? (
            <>
              {/* Poll Form */}
              <div className="form-group">
                <label htmlFor="title">Question *</label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What would you like to ask?"
                  required
                />
              </div>

              <div className="form-group">
                <label>Answer Type</label>
                <div className="poll-type-options">
                  <label className={`poll-type-option ${pollType === 'yesno' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="pollType"
                      value="yesno"
                      checked={pollType === 'yesno'}
                      onChange={(e) => setPollType(e.target.value)}
                    />
                    <span>Yes / No</span>
                  </label>
                  <label className={`poll-type-option ${pollType === 'custom' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="pollType"
                      value="custom"
                      checked={pollType === 'custom'}
                      onChange={(e) => setPollType(e.target.value)}
                    />
                    <span>Custom Options</span>
                  </label>
                </div>
              </div>

              {pollType === 'custom' && (
                <div className="form-group">
                  <label>Options</label>
                  <div className="poll-options-list">
                    {pollOptions.map((option, index) => (
                      <div key={index} className="poll-option-input">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => updatePollOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                        />
                        {pollOptions.length > 2 && (
                          <button
                            type="button"
                            className="remove-option-btn"
                            onClick={() => removePollOption(index)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    {pollOptions.length < 6 && (
                      <button
                        type="button"
                        className="add-option-btn"
                        onClick={addPollOption}
                      >
                        <Plus size={16} />
                        Add option
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="end-time-toggle">
                  <input
                    type="checkbox"
                    checked={hasEndTime}
                    onChange={(e) => setHasEndTime(e.target.checked)}
                  />
                  <Clock size={16} />
                  <span>Set an end time (optional)</span>
                </label>

                {hasEndTime && (
                  <div className="end-time-options">
                    <div className="end-time-presets">
                      {[
                        { value: '1d', label: '24 hours' },
                        { value: '3d', label: '3 days' },
                        { value: '1w', label: '1 week' },
                        { value: '2w', label: '2 weeks' },
                        { value: 'custom', label: 'Custom' },
                      ].map(preset => (
                        <button
                          key={preset.value}
                          type="button"
                          className={`preset-chip ${endTimePreset === preset.value ? 'selected' : ''}`}
                          onClick={() => setEndTimePreset(preset.value)}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>

                    {endTimePreset === 'custom' && (
                      <input
                        type="datetime-local"
                        className="end-time-custom-input"
                        value={customEndTime}
                        min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                        onChange={(e) => setCustomEndTime(e.target.value)}
                      />
                    )}

                    {(() => {
                      const dt = computeExpiresAt()
                      if (!dt) return null
                      return (
                        <p className="end-time-preview">
                          Voting closes{' '}
                          {dt.toLocaleString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </p>
                      )
                    })()}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Announcement Form */}
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Announcement title..."
                  required
                  data-walk="announcement-title-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="content">Content *</label>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <button
                    type="button"
                    onClick={handleInsertLink}
                    title="Add link"
                    aria-label="Add link"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 8px',
                      fontSize: 13,
                      background: 'var(--color-bg-alt, #f3f4f6)',
                      border: '1px solid var(--color-border, #e5e7eb)',
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                  >
                    <LinkIcon size={14} />
                    <span>Add link</span>
                  </button>
                </div>
                <textarea
                  id="content"
                  ref={contentRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write your announcement..."
                  rows={6}
                  required
                  data-walk="announcement-content-textarea"
                />
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Select text and click the link icon to add a link.
                </p>
              </div>

              {/* Optional companion calendar event — create-mode only.
                  When enabled and startAt is filled in, submitting will
                  also add a doc to the `events` collection so the
                  announcement shows up on the Events calendar. */}
              {!isEdit && (
                <div className="form-group">
                  <label
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                      background: 'var(--color-info-bg)',
                      border: '1px solid var(--color-info-border)',
                      borderRadius: 10,
                      padding: '12px 14px',
                      marginTop: 8,
                      marginBottom: addToCalendar ? 12 : 16,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={addToCalendar}
                      onChange={(e) => setAddToCalendar(e.target.checked)}
                      style={{ marginTop: 3, flexShrink: 0, width: 18, height: 18, cursor: 'pointer' }}
                    />
                    <Calendar
                      size={20}
                      color="var(--color-info-text)"
                      style={{ flexShrink: 0, marginTop: 2 }}
                    />
                    <div style={{ lineHeight: 1.35 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text-primary)' }}>
                        Add to Calendar?
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--color-text-secondary)', marginTop: 3 }}>
                        {addToCalendar
                          ? 'This announcement will also appear on the neighborhood calendar as an event.'
                          : 'Also add this announcement to the neighborhood calendar as an event.'}
                      </div>
                    </div>
                  </label>

                  {addToCalendar && (
                    <div
                      style={{
                        marginTop: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        padding: 14,
                        border: '1px solid var(--color-border)',
                        borderRadius: 12,
                        background: 'var(--color-bg-alt)',
                      }}
                    >
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <label
                            htmlFor="event-start"
                            style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}
                          >
                            Start time *
                          </label>
                          <input
                            id="event-start"
                            type="datetime-local"
                            value={eventStart}
                            onChange={(e) => setEventStart(e.target.value)}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="event-end"
                            style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}
                          >
                            End time
                          </label>
                          <input
                            id="event-end"
                            type="datetime-local"
                            value={eventEnd}
                            min={eventStart || undefined}
                            onChange={(e) => setEventEnd(e.target.value)}
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="event-location"
                          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, marginBottom: 6 }}
                        >
                          <MapPin size={14} />
                          Location
                        </label>
                        <input
                          id="event-location"
                          type="text"
                          value={eventLocation}
                          onChange={(e) => setEventLocation(e.target.value)}
                          placeholder="e.g. Clubhouse, 123 Main St"
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/*
            Email notice — mirrors AnnouncementEmailNoticeSection on iOS
            and AnnouncementEmailNoticeCard on Android so the reminder
            wording is identical across all three platforms. Only shown
            in create mode: the Cloud Function that sends the blast
            (sendAnnouncementEmail) triggers on onDocumentCreated, so
            editing an existing announcement does not resend, and
            displaying this notice in edit mode would be misleading.
          */}
          {!isEdit && (
            <label
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                background: 'var(--color-info-bg)',
                border: '1px solid var(--color-info-border)',
                borderRadius: 10,
                padding: '12px 14px',
                marginTop: 8,
                marginBottom: 16,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={emailNeighbors}
                onChange={(e) => setEmailNeighbors(e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0, width: 18, height: 18, cursor: 'pointer' }}
                data-walk="announcement-email-checkbox"
              />
              <Mail
                size={20}
                color="var(--color-info-text)"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <div style={{ lineHeight: 1.35 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text-primary)' }}>
                  {isPoll
                    ? 'Email this poll to all active neighbors'
                    : 'Email this announcement to all active neighbors'}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--color-text-secondary)', marginTop: 3 }}>
                  {emailNeighbors
                    ? (isPoll
                        ? 'Neighbors receive the question and options in their inbox — voting still happens in the app.'
                        : 'Neighbors receive the full announcement in their inbox as soon as you click ' +
                          (isPoll ? 'Create Poll.' : 'Post Announcement.'))
                    : 'No email will be sent. The ' + (isPoll ? 'poll' : 'announcement') + ' will only appear in the app.'}
                </div>
              </div>
            </label>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={handleCloseAttempt}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading} data-walk="announcement-submit-btn">
              {loading ? (
                <>
                  <Loader size={18} className="spinner" />
                  <span>{submittingLabel}</span>
                </>
              ) : (
                <>
                  {isPoll ? <BarChart3 size={18} /> : <Megaphone size={18} />}
                  <span>{submitLabel}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {linkDialogOpen && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1100 }}
          onClick={() => setLinkDialogOpen(false)}
        >
          <div
            className="modal"
            style={{ maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Add link</h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setLinkDialogOpen(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="link-label">Link text</label>
                <input
                  id="link-label"
                  type="text"
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  placeholder="e.g. click here"
                  autoFocus
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="link-url">URL *</label>
                <input
                  id="link-url"
                  type="text"
                  value={linkUrl}
                  onChange={(e) => {
                    setLinkUrl(e.target.value)
                    if (linkError) setLinkError('')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      confirmInsertLink()
                    }
                  }}
                  placeholder="https://example.com"
                />
              </div>
              {linkError && (
                <div style={{ color: '#DC2626', fontSize: 13 }}>{linkError}</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setLinkDialogOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={confirmInsertLink}
                >
                  Add link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
